import { useHomepageVisibility } from '../utils/homepageVisibility';
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axiosInstance from '../api/axios';
import useHairCollections from '../hooks/useHairCollections';

/**
 * Showcase aller Kollektions-Kategorien auf der Homepage.
 * Eine große Hero-Card pro Kategorie → linkt zu /collections (Tab vorausgewählt).
 * Erscheint NUR wenn ≥2 aktive Kategorien existieren.
 */
const CollectionCategoriesShowcaseInner = () => {
  const { collections } = useHairCollections();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axiosInstance
      .get('/hair-collections/categories')
      .then((res) => {
        const cats = Array.isArray(res.data) ? res.data.filter((c) => c.is_active !== false) : [];
        setCategories(cats);
      })
      .catch(() => {});
  }, []);

  // For each category: count collections + pick a representative palette
  const cardsData = useMemo(() => {
    return categories.map((cat) => {
      const itemsInCat = (collections || []).filter(
        (c) => (c.category || 'limited-hair') === cat.slug && c.is_active !== false,
      );
      // Use the first collection's palette as background hint
      const firstPalette = itemsInCat[0]?.palette || null;
      return {
        ...cat,
        count: itemsInCat.length,
        bg: firstPalette?.bg || '#F5EDE4',
        text: firstPalette?.text || '#5D4E3C',
        isSeasonal: cat.slug === 'limited-seasonal',
      };
    });
  }, [categories, collections]);

  // Seasonal goes first as the hero "Premium" tile when present
  const sortedCards = useMemo(() => {
    const seasonal = cardsData.filter((c) => c.isSeasonal);
    const rest = cardsData.filter((c) => !c.isSeasonal);
    return [...seasonal, ...rest];
  }, [cardsData]);

  if (cardsData.length < 2) return null;

  return (
    <section
      className="py-10 md:py-16 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: '#FAF5EE' }}
      data-testid="categories-showcase"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-6 md:mb-10">
          <p className="text-[11px] tracking-[5px] uppercase mb-2" style={{ color: '#C4A084' }}>
            ✨ Unsere Kollektionen
          </p>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#5D4E3C]"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {sortedCards.some((c) => c.isSeasonal) ? 'Fünf Welten · ein Stil' : 'Vier Welten · ein Stil'}
          </h2>
          <p className="text-sm md:text-base text-[#8B7355] mt-2 max-w-2xl mx-auto">
            Entdecke kuratierte Boxen passend zu deinem Lebensgefühl — vom verspielten Haaraccessoire
            bis zur stilvollen Home-Atmosphäre. Krönung der Reihe: unsere Saison-Editionen.
          </p>
        </div>

        <div className="grid gap-3 md:gap-4 grid-cols-2 lg:grid-cols-4">
          {sortedCards.map((cat) => {
            const isHero = cat.isSeasonal;
            return (
              <Link
                key={cat.slug}
                to="/collections"
                state={{ activeCategory: cat.slug }}
                className={`group relative overflow-hidden transition-transform hover:-translate-y-1 ${
                  isHero
                    ? 'col-span-2 lg:col-span-4 aspect-[16/9] md:aspect-[21/9]'
                    : 'aspect-[3/4] md:aspect-[4/5]'
                }`}
                style={{
                  background: isHero
                    ? `linear-gradient(120deg, #FCE8EF 0%, #FFF6D6 33%, #F5E3D0 66%, #E6EEF5 100%)`
                    : `linear-gradient(135deg, ${cat.bg} 0%, ${cat.accent_color}33 100%)`,
                  color: cat.text,
                }}
                data-testid={`showcase-card-${cat.slug}`}
              >
                {isHero && (
                  <>
                    {/* Seasonal floating symbols */}
                    <div className="pointer-events-none absolute inset-0 opacity-60">
                      <span className="absolute top-3 left-5 text-2xl md:text-3xl">🌸</span>
                      <span className="absolute top-4 right-6 text-2xl md:text-3xl">☀️</span>
                      <span className="absolute bottom-5 left-8 text-2xl md:text-3xl">🍂</span>
                      <span className="absolute bottom-3 right-5 text-2xl md:text-3xl">❄️</span>
                    </div>
                    {/* Premium ribbon */}
                    <div
                      className="absolute top-3 right-3 text-[10px] md:text-[11px] tracking-[3px] uppercase font-bold px-2 py-1"
                      style={{ background: '#1F1A14', color: '#F2D88A' }}
                    >
                      ★ Premium
                    </div>
                  </>
                )}
                <div className={`absolute inset-0 ${isHero ? 'p-6 md:p-10' : 'p-5 md:p-7'} flex flex-col ${isHero ? 'items-center justify-center text-center' : ''}`}>
                  {!isHero && <div className="text-4xl md:text-5xl mb-2">{cat.emoji}</div>}
                  <p
                    className={`${isHero ? 'text-[10px] md:text-[12px] tracking-[4px]' : 'text-[9px] md:text-[10px] tracking-[2px] md:tracking-[3px]'} uppercase font-semibold`}
                    style={{ color: cat.accent_color }}
                  >
                    {isHero ? 'Vier Saisons · Eine Premium-Linie' : `${cat.count} ${cat.count === 1 ? 'Edition' : 'Editions'}`}
                  </p>
                  <h3
                    className={`${isHero ? 'text-2xl md:text-4xl' : 'text-lg md:text-2xl'} font-bold ${isHero ? 'mt-2' : 'mt-1'} leading-tight`}
                    style={{ fontFamily: 'Georgia, serif', color: '#3A2F22' }}
                  >
                    {cat.name}
                  </h3>
                  {cat.tagline && (
                    <p className={`${isHero ? 'text-xs md:text-base mt-3 max-w-xl' : 'text-xs md:text-sm mt-2'} opacity-80 leading-snug`} style={{ color: '#5D4E3C' }}>
                      {cat.tagline}
                    </p>
                  )}
                  <div
                    className={`${isHero ? 'mt-4' : 'mt-auto'} inline-flex items-center gap-1 text-[10px] md:text-xs tracking-[2px] uppercase font-semibold`}
                    style={{ color: cat.accent_color }}
                  >
                    {isHero ? 'Saison entdecken' : 'Entdecken'}
                    <ArrowRight className="h-3 w-3" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const CollectionCategoriesShowcase = () => {
  const visibility = useHomepageVisibility();
  return visibility.show_collections ? <CollectionCategoriesShowcaseInner /> : null;
};

export default CollectionCategoriesShowcase;
